import { useLayoutEffect, useMemo, useState, type RefObject } from 'react';
import { buildSelectedValuesKey, countChipsThatFit, getOverflowValues } from '../utils';

interface UseMultiSelectChipsOverflowParams {
    allValuesVisible: boolean;
    selectedValues: unknown[];
    containerRef: RefObject<HTMLDivElement | null>;
}

interface UseMultiSelectChipsOverflowResult {
    visibleCount: number;
    overflow: unknown[];
}

export function useMultiSelectChipsOverflow({
    allValuesVisible,
    selectedValues,
    containerRef
}: UseMultiSelectChipsOverflowParams): UseMultiSelectChipsOverflowResult {
    const [visibleCount, setVisibleCount] = useState(selectedValues.length);
    const valuesKey = useMemo(
        () => (allValuesVisible ? '' : buildSelectedValuesKey(selectedValues)),
        [allValuesVisible, selectedValues]
    );

    useLayoutEffect(() => {
        if (allValuesVisible || selectedValues.length === 0) {
            return;
        }

        const container = containerRef.current;
        if (!container) {
            return;
        }

        function updateVisibleCount() {
            if (!containerRef.current) {
                return;
            }
            const next = countChipsThatFit(containerRef.current);
            setVisibleCount((prev) => (prev === next ? prev : next));
        }

        updateVisibleCount();
        const observer = new ResizeObserver(updateVisibleCount);
        observer.observe(container);
        return () => observer.disconnect();
    }, [allValuesVisible, selectedValues.length, valuesKey, containerRef]);

    const overflow = getOverflowValues(selectedValues, visibleCount, allValuesVisible);

    return { visibleCount, overflow };
}
